// Vue
import { ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { IndicatorTypeMappingType, IndicatorTypeType, DenominatorUnitType } from "../types/IndicatorTypes";

// GraphQL
import { GetIndicatorTypeMappingsQuery } from "@/graphql";

interface IndicatorsProvider {
  // Getters
  getIndicatorTypeMappings: () => IndicatorTypeMappingType[];

  // Methods
  getDefaultIndicatorTypeMapping: (calculationTypeId: number) => IndicatorTypeMappingType | undefined;
  getIndicatorUnit: (
    indicatorType: Pick<IndicatorTypeType, "unit">,
    denominatorUnit?: Pick<DenominatorUnitType, "unit">
  ) => string;
  getLegacyGWPUnit: () => string;
  getIdentifierFromMappingId: (mappingId: string) => string;
  getIdentifierFromIndicatorTypeId: (indicatorTypeId: number) => string;
  getIndicatorTypeMappingsFromIdentifier: (identifier: string) => IndicatorTypeMappingType[] | [];
  getUniqueIdentifiers: () => string[];
  getMappingIdsFromIdentifier: (identifier: string) => string[];
  getUniqueIndicatorTypeMappings: () => IndicatorTypeMappingType[];
  getIndicatorTypeFromMappingId: (mappingId: string) => IndicatorTypeType | undefined;
  mapData: (data: GetIndicatorTypeMappingsQuery) => void;

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useIndicators = defineStore("IndicatorsProvider", (): IndicatorsProvider => {
  // ///////////////////
  // Refs
  // ///////////////////

  const indicatorTypeMappings = ref<IndicatorTypeMappingType[]>([]);

  // ///////////////////
  // Getters
  // ///////////////////

  function getIndicatorTypeMappings() {
    return indicatorTypeMappings.value;
  }

  // ///////////////////
  // Setters
  // ///////////////////

  function setIndicatorTypeMappings(newIndicatorTypeMappings: IndicatorTypeMappingType[]) {
    indicatorTypeMappings.value = newIndicatorTypeMappings;
  }

  // ///////////////////
  // Methods
  // ///////////////////

  function mapData(data: GetIndicatorTypeMappingsQuery) {
    const { indicatorTypeMapping } = data;

    if (!indicatorTypeMapping) return;

    // Set default data
    setIndicatorTypeMappings(indicatorTypeMapping as IndicatorTypeMappingType[]);
  }

  function getDefaultIndicatorTypeMapping(calculationTypeId: number) {
    return indicatorTypeMappings.value.find((mapping) => mapping.calculationType.map((x) => x.id).includes(calculationTypeId));
  }

  function getIndicatorUnit(indicatorType: Pick<IndicatorTypeType, "unit">, denominatorUnit?: Pick<DenominatorUnitType, "unit">) {
    if (denominatorUnit?.unit) {
      return `${indicatorType.unit}/${denominatorUnit.unit}`;
    }
    return indicatorType.unit || "";
  }

  function getIdentifierFromMappingId(mappingId: string) {
    const mapping = indicatorTypeMappings.value.find((mapping) => mapping.id === mappingId);
    return mapping?.indicatorType.identifier || "";
  }

  function getIdentifierFromIndicatorTypeId(indicatorTypeId: number) {
    const mapping = indicatorTypeMappings.value.find((mapping) => mapping.indicatorType.id === indicatorTypeId);
    return mapping?.indicatorType.identifier || "";
  }

  function getIndicatorTypeMappingsFromIdentifier(identifier: string) {
    return indicatorTypeMappings.value.filter((mapping) => mapping.indicatorType.identifier === identifier);
  }

  function getUniqueIdentifiers(): string[] {
    return Array.from(new Set(indicatorTypeMappings.value.map((mapping) => mapping.indicatorType.identifier))) as string[];
  }

  function getUniqueIndicatorTypeMappings(): IndicatorTypeMappingType[] {
    const uniqueIdentifiers = getUniqueIdentifiers();
    return uniqueIdentifiers.map((identifier) => {
      const mappings = getIndicatorTypeMappingsFromIdentifier(identifier);
      return mappings[0];
    });
  }

  function getMappingIdsFromIdentifier(identifier: string) {
    return indicatorTypeMappings.value
      .filter((mapping) => mapping.indicatorType.identifier === identifier)
      .map((mapping) => mapping.id);
  }

  function getIndicatorTypeFromMappingId(mappingId: string) {
    const mapping = indicatorTypeMappings.value.find((mapping) => mapping.id === mappingId);
    if (!mapping) return undefined;
    return mapping?.indicatorType;
  }

  function getLegacyGWPUnit() {
    return "kgCO₂-eq";
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const loading = ref<boolean>(false);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // ///////////////////
  // Returns
  // ///////////////////

  return {
    // Getters
    getIndicatorTypeMappings,

    // Methods
    getDefaultIndicatorTypeMapping,
    getIndicatorUnit,
    getIdentifierFromMappingId,
    getIdentifierFromIndicatorTypeId,
    getIndicatorTypeMappingsFromIdentifier,
    getUniqueIdentifiers,
    getUniqueIndicatorTypeMappings,
    getMappingIdsFromIdentifier,
    getLegacyGWPUnit,
    getIndicatorTypeFromMappingId,
    mapData,

    // Utility
    getLoading,
    setLoading,
  };
});

export default useIndicators;
